<template>
  <div v-if="show && !tempHidden" class="modal fade show" tabindex="-1" style="display: block;" @click.self="closeModal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-person-badge me-2"></i>
            Impersonate User
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Warning Message -->
          <div class="alert alert-warning d-flex align-items-center mb-3">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            <div>
              <strong>Security Notice:</strong> All impersonation activities are logged for audit purposes. Use this feature responsibly.
            </div>
          </div>

          <!-- Search Input -->
          <div class="mb-3">
            <label class="form-label">Search for user</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search by name, email, or employee ID..."
                @input="debouncedSearch"
              />
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mt-2">Searching users...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-circle me-2"></i>
            {{ error }}
          </div>

          <!-- Users List -->
          <div v-else-if="users.length > 0" class="user-list">
            <div class="list-group list-group-flush">
              <button
                v-for="user in users"
                :key="user.id"
                type="button"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                @click="selectUser(user)"
                :disabled="impersonating"
              >
                <div class="flex-grow-1">
                  <div class="d-flex w-100 justify-content-between">
                    <h6 class="mb-1">{{ user.full_name }}</h6>
                    <small class="text-muted">{{ user.employee_id }}</small>
                  </div>
                  <p class="mb-1 text-muted small">{{ user.email }}</p>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-secondary">{{ user.role }}</span>
                    <span v-if="user.department" class="badge bg-light text-dark">
                      {{ user.department }}
                    </span>
                    <span v-if="user.job_title" class="text-muted small">
                      {{ user.job_title }}
                    </span>
                  </div>
                </div>
                <div class="ms-3">
                  <i class="bi bi-arrow-right-circle text-primary"></i>
                </div>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="searchQuery" class="text-center py-4">
            <i class="bi bi-search display-4 text-muted"></i>
            <p class="text-muted mt-2">No users found matching "{{ searchQuery }}"</p>
          </div>

          <!-- Initial State -->
          <div v-else class="text-center py-4">
            <i class="bi bi-person-circle display-4 text-muted"></i>
            <p class="text-muted mt-2">Search for a user to impersonate</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div v-if="show && !tempHidden" class="modal-backdrop fade show"></div>

  <!-- Confirmation Modal -->
  <ConfirmModal ref="confirmModal" />
</template>

<script setup>
import { ref, watch } from 'vue';
import { useImpersonation } from '@/composables/useImpersonation';
import ConfirmModal from '@/components/shared/ConfirmModal.vue';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'impersonate']);

const {
  loading,
  error,
  searchUsers,
  startImpersonation
} = useImpersonation();

const toast = useToast();
const confirmModal = ref(null);
const searchQuery = ref('');
const users = ref([]);
const impersonating = ref(false);
const tempHidden = ref(false); // Temporarily hide modal when showing confirmation

let searchTimeout = null;

// Debounced search function
function debouncedSearch() {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(async () => {
    if (searchQuery.value.trim()) {
      users.value = await searchUsers(searchQuery.value);
    } else {
      users.value = [];
    }
  }, 300);
}

// Select user to impersonate
async function selectUser(user) {
  // Temporarily hide the impersonation modal so confirmation modal is visible
  tempHidden.value = true;

  const confirmed = await confirmModal.value.showModal({
    title: 'Start Impersonation',
    message: `Are you sure you want to impersonate ${user.full_name}?`,
    details: `Email: ${user.email}\nRole: ${user.role}\n\nAll actions will be performed as this user and will be logged for audit purposes.`,
    confirmButtonText: 'Start Impersonation',
    confirmButtonClass: 'btn-primary'
  });

  if (confirmed) {
    impersonating.value = true;
    const result = await startImpersonation(user.id);

    if (result.success) {
      emit('impersonate', user);
      closeModal();
    } else {
      impersonating.value = false;
      tempHidden.value = false; // Show modal again on error
      toast.error(result.error || 'Failed to start impersonation');
    }
  } else {
    // User cancelled - show the modal again
    tempHidden.value = false;
  }
}

// Close modal
function closeModal() {
  emit('close');
  searchQuery.value = '';
  users.value = [];
  tempHidden.value = false;
}

// Load initial users when modal opens
watch(() => props.show, async (newValue) => {
  if (newValue) {
    users.value = await searchUsers('');
  }
});
</script>

<style scoped>
.user-list {
  max-height: 400px;
  overflow-y: auto;
}

.list-group-item {
  transition: background-color 0.2s;
}

.list-group-item:hover:not(:disabled) {
  background-color: #f8f9fa;
  cursor: pointer;
}

.list-group-item:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.badge {
  font-size: 0.75rem;
}

/* Primary button styling */
.btn-primary {
  background: var(--primary-color, #7B8FD4);
  border-color: var(--primary-color, #7B8FD4);
}

.btn-primary:hover {
  background: #6a7fc7;
  border-color: #6a7fc7;
}
</style>
